import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Shield, BarChart3 } from 'lucide-react';

const CONSENT_KEY = 'wt_cookie_consent';
const GA_MEASUREMENT_ID = 'G-XXXXXXXXXX'; // Replace with your Google Analytics Measurement ID

const loadGoogleAnalytics = () => {
  if (window._gaLoaded || GA_MEASUREMENT_ID === 'G-XXXXXXXXXX') return;
  window._gaLoaded = true;

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(script);

  window.dataLayer = window.dataLayer || [];
  function gtag() { window.dataLayer.push(arguments); }
  window.gtag = gtag;
  gtag('js', new Date());
  gtag('config', GA_MEASUREMENT_ID, { anonymize_ip: true });
};

const getStoredConsent = () => {
  try {
    const stored = localStorage.getItem(CONSENT_KEY);
    if (!stored) return null;
    const consent = JSON.parse(stored);
    // Re-prompt after 12 months per GDPR best practice
    const storedDate = new Date(consent.timestamp);
    const monthsSince = (Date.now() - storedDate.getTime()) / (1000 * 60 * 60 * 24 * 365);
    if (monthsSince >= 1) {
      localStorage.removeItem(CONSENT_KEY);
      return null;
    }
    return consent;
  } catch {
    return null;
  }
};

const Toggle = ({ checked, onChange, disabled }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    disabled={disabled}
    onClick={() => !disabled && onChange(!checked)}
    className={`relative inline-flex h-7 w-12 shrink-0 rounded-full transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3A4A3F] ${
      disabled ? 'cursor-not-allowed opacity-70' : 'cursor-pointer'
    } ${checked ? 'bg-[#3A4A3F]' : 'bg-[#D1D5DB]'}`}
    data-testid={disabled ? 'toggle-essential' : 'toggle-analytics'}
  >
    <span
      className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow-md ring-0 transition-transform duration-200 mt-1 ${
        checked ? 'translate-x-6 ml-0.5' : 'translate-x-0.5'
      }`}
    />
  </button>
);

export const CookieConsent = () => {
  const [visible, setVisible] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [analyticsEnabled, setAnalyticsEnabled] = useState(false);

  useEffect(() => {
    const consent = getStoredConsent();
    if (!consent) {
      const timer = setTimeout(() => setVisible(true), 800);
      return () => clearTimeout(timer);
    }
    if (consent.analytics) {
      loadGoogleAnalytics();
    }
  }, []);

  const saveConsent = useCallback((analytics) => {
    const consent = {
      essential: true,
      analytics,
      timestamp: new Date().toISOString(),
    };
    localStorage.setItem(CONSENT_KEY, JSON.stringify(consent));
    if (analytics) loadGoogleAnalytics();
    setVisible(false);
    setShowPreferences(false);
  }, []);

  const acceptAll = () => saveConsent(true);
  const rejectNonEssential = () => saveConsent(false);
  const savePreferences = () => saveConsent(analyticsEnabled);

  if (!visible) return null;

  return (
    <>
      <AnimatePresence>
        {showPreferences && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[60] bg-black/30 backdrop-blur-sm"
            onClick={() => setShowPreferences(false)}
            data-testid="preferences-overlay"
          >
            <motion.div
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
              className="fixed bottom-0 left-0 right-0 bg-white rounded-t-3xl shadow-2xl max-h-[85vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
              data-testid="preferences-panel"
            >
              <div className="max-w-2xl mx-auto px-6 py-8 md:px-8 md:py-10">
                <div className="flex items-center justify-between mb-8">
                  <h3 className="font-serif text-2xl font-semibold text-[#2C332B]">
                    Cookie Preferences
                  </h3>
                  <button
                    onClick={() => setShowPreferences(false)}
                    className="p-2 rounded-full hover:bg-[#F9F8F6] transition-colors"
                    data-testid="preferences-close"
                  >
                    <X className="w-5 h-5 text-[#6C757D]" />
                  </button>
                </div>

                <p className="text-[#6C757D] leading-relaxed mb-8">
                  Choose which cookies you'd like to allow. Essential cookies are always
                  active as they are necessary for the website to function correctly.
                </p>

                <div className="space-y-6">
                  {/* Essential Cookies */}
                  <div className="bg-[#F9F8F6] rounded-2xl p-6">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-start gap-3 flex-1">
                        <Shield className="w-5 h-5 text-[#3A4A3F] mt-0.5 shrink-0" />
                        <div>
                          <h4 className="font-semibold text-[#2C332B] mb-1">Essential Cookies</h4>
                          <p className="text-sm text-[#6C757D] leading-relaxed">
                            Required for the website to function. These cookies enable core
                            features such as page navigation, remembering your cookie preferences,
                            and accessing secure areas. The website cannot function properly
                            without these cookies.
                          </p>
                        </div>
                      </div>
                      <Toggle checked={true} onChange={() => {}} disabled />
                    </div>
                    <span className="inline-block mt-3 ml-8 text-xs font-medium text-[#3A4A3F] bg-[#3A4A3F]/10 rounded-full px-3 py-1">
                      Always Active
                    </span>
                  </div>

                  {/* Analytics Cookies */}
                  <div className="bg-[#F9F8F6] rounded-2xl p-6">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-start gap-3 flex-1">
                        <BarChart3 className="w-5 h-5 text-[#D4A373] mt-0.5 shrink-0" />
                        <div>
                          <h4 className="font-semibold text-[#2C332B] mb-1">Analytics Cookies</h4>
                          <p className="text-sm text-[#6C757D] leading-relaxed">
                            Help us understand how visitors interact with our website. These
                            cookies collect information anonymously and allow us to improve
                            your experience by understanding which pages are most popular and
                            how visitors navigate the site.
                          </p>
                        </div>
                      </div>
                      <Toggle checked={analyticsEnabled} onChange={setAnalyticsEnabled} />
                    </div>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-3 mt-8">
                  <button
                    onClick={savePreferences}
                    className="flex-1 px-6 py-3 text-base font-medium bg-[#3A4A3F] text-white rounded-full hover:bg-[#2C332B] transition-all duration-300"
                    data-testid="save-preferences-btn"
                  >
                    Save Preferences
                  </button>
                  <button
                    onClick={acceptAll}
                    className="flex-1 px-6 py-3 text-base font-medium border border-[#3A4A3F]/20 text-[#3A4A3F] rounded-full hover:bg-[#3A4A3F]/5 transition-all duration-300"
                    data-testid="accept-all-preferences-btn"
                  >
                    Accept All
                  </button>
                </div>

                <div className="flex gap-4 justify-center mt-6 text-sm">
                  <Link to="/privacy-policy" onClick={() => { setShowPreferences(false); setVisible(false); }} className="text-[#D4A373] hover:underline">Privacy Policy</Link>
                  <Link to="/cookie-policy" onClick={() => { setShowPreferences(false); setVisible(false); }} className="text-[#D4A373] hover:underline">Cookie Policy</Link>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main Banner */}
      {!showPreferences && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
          className="fixed bottom-0 left-0 right-0 z-50"
          data-testid="cookie-banner"
        >
          <div className="bg-white/95 backdrop-blur-xl shadow-[0_-4px_30px_rgba(0,0,0,0.08)] border-t border-[#E8E4DD]">
            <div className="max-w-6xl mx-auto px-6 py-6 md:py-8">
              <div className="flex flex-col lg:flex-row gap-6 lg:gap-8 items-start lg:items-center">
                <div className="flex-1 min-w-0">
                  <h3 className="font-serif text-xl font-semibold text-[#2C332B] mb-2">
                    We Value Your Privacy
                  </h3>
                  <p className="text-sm text-[#6C757D] leading-relaxed">
                    We use cookies to help our website work properly, understand how visitors
                    use our site and improve your experience. You can accept all cookies, reject
                    non-essential cookies, or manage your preferences at any time.
                  </p>
                  <div className="flex gap-4 mt-2">
                    <Link to="/privacy-policy" onClick={() => setVisible(false)} className="text-sm text-[#D4A373] hover:underline">Privacy Policy</Link>
                    <Link to="/cookie-policy" onClick={() => setVisible(false)} className="text-sm text-[#D4A373] hover:underline">Cookie Policy</Link>
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row gap-3 shrink-0 w-full lg:w-auto">
                  <button
                    onClick={acceptAll}
                    className="px-6 py-3 text-sm font-medium bg-[#3A4A3F] text-white rounded-full hover:bg-[#2C332B] transition-all duration-300 hover:scale-[1.02]"
                    data-testid="cookie-accept-all"
                  >
                    Accept All
                  </button>
                  <button
                    onClick={rejectNonEssential}
                    className="px-6 py-3 text-sm font-medium border border-[#3A4A3F]/20 text-[#3A4A3F] rounded-full hover:bg-[#3A4A3F]/5 transition-all duration-300"
                    data-testid="cookie-reject"
                  >
                    Reject Non-Essential
                  </button>
                  <button
                    onClick={() => setShowPreferences(true)}
                    className="px-6 py-3 text-sm font-medium text-[#D4A373] hover:underline transition-all duration-300"
                    data-testid="cookie-manage"
                  >
                    Manage Preferences
                  </button>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </>
  );
};
